/* eslint-disable jsx-a11y/anchor-is-valid */
import React, {useEffect, useState} from 'react'
import {useDispatch, useSelector, shallowEqual} from 'react-redux'
import {RootState} from '../../../setup'
import {useFormik} from 'formik'
import * as Yup from 'yup'
import {ILeadStatusState} from './redux/LeadStatusRedux'
import {actions} from './redux/LeadStatusRedux'
import {LeadStatusModel} from './models/LeadStatusModel'
import {PageTitle} from '../../../_metronic/layout/core'
import LeadStatusRow from './LeadStatusRow'
import {Modal} from 'react-bootstrap'

const LeadStatusPage: React.FC = () => {
  const dispatch = useDispatch()
  const [showModal, setShowModal] = useState(false)

  const leadStatus: ILeadStatusState = useSelector<RootState>(
    ({leadStatus}) => leadStatus,
    shallowEqual
  ) as ILeadStatusState

  useEffect(() => {
    dispatch(actions.fetchPage(1))
  }, [dispatch])

  const changePage = (page: number) => dispatch(actions.fetchPage(page))

  const getPageList = () => {
    let elements: JSX.Element[] = []
    leadStatus.visiblePageList.forEach((page) => {
      elements.push(
        <li
          key={page}
          className={page === leadStatus.currentPage ? 'page-item active' : 'page-item'}
        >
          <a href='#' className='page-link' onClick={() => changePage(page)}>
            {page}
          </a>
        </li>
      )
    })
    return elements
  }

  const getNextPageButton = () => {
    if (leadStatus.visiblePageList[leadStatus.visiblePageList.length - 1] < leadStatus.totalPages) {
      return (
        <li className='page-item next'>
          <a href='#' onClick={() => changePage(++leadStatus.currentPage)} className='page-link'>
            <i className='next'></i>
          </a>
        </li>
      )
    }
  }

  const getPreviousPageButton = () => {
    if (leadStatus.visiblePageList[0] > 1) {
      return (
        <li className='page-item previous'>
          <a href='#' onClick={() => changePage(--leadStatus.currentPage)} className='page-link'>
            <i className='previous'></i>
          </a>
        </li>
      )
    }
  }

  const getEmptyTable = () => {
    if (!leadStatus.leadStatuses || leadStatus.leadStatuses.length === 0) {
      return (
        <div className='row justify-content-md-center mt-20 mb-20'>
          <div className='col-md-auto'>No lead statuses found. Add your first lead status.</div>
        </div>
      )
    }
  }

  const getRows = () => {
    let elements: JSX.Element[] = []
    if (leadStatus.leadStatuses && leadStatus.leadStatuses.length > 0) {
      leadStatus.leadStatuses.forEach((status: LeadStatusModel, index: number) => {
        elements.push(
          <LeadStatusRow
            key={index}
            leadStatus={status}
            index={`${leadStatus.currentPage}-${index}`}
          />
        )
      })
      return elements
    }
  }

  const addLeadStatusSchema = Yup.object().shape({
    status: Yup.string()
      .min(2, 'Minimum 2 characters')
      .max(50, 'Maximum 50 characters')
      .required('Status is required'),
  })

  const initialValues = {
    status: '',
  }

  const formik = useFormik({
    initialValues,
    validationSchema: addLeadStatusSchema,
    onSubmit: (values, {setSubmitting, resetForm}) => {
      dispatch(actions.createLeadStatus(values.status))
      setSubmitting(false)
      resetForm()
      setShowModal(false)
    },
  })

  return (
    <>
      <PageTitle breadcrumbs={[]}>{'Lead Status'}</PageTitle>
      <div className={`card mb-5 mb-xl-8`}>
        {/* begin::Header */}
        <div className='card-header border-0 pt-5'>
          <h3 className='card-title align-items-start flex-column'>
            <span className='card-label fw-bolder fs-3 mb-1'>Lead Statuses</span>
          </h3>
          <div className='card-toolbar'>
            <button className='btn btn-sm btn-primary' onClick={() => setShowModal(true)}>
              Add New Status
            </button>
          </div>
        </div>
        {/* end::Header */}
        {/* begin::Body */}
        <div className='card-body py-3'>
          {getEmptyTable()}
          {/* begin::Table container */}
          <div className='table-responsive'>
            {/* begin::Table */}
            <table className='table table-row-bordered table-row-gray-100 align-middle gs-0 gy-3'>
              {/* begin::Table head */}
              <thead>
                <tr className='fw-bolder text-muted'>
                  <th className='min-w-150px'>Status</th>
                  <th className='text-end min-w-100px'>Actions</th>
                </tr>
              </thead>
              {/* end::Table head */}
              {/* begin::Table body */}
              <tbody>{getRows()}</tbody>
              {/* end::Table body */}
            </table>
            {/* end::Table */}
          </div>
          {/* end::Table container */}
          {leadStatus.totalPages > 1 && (
            <div className='d-flex flex-stack flex-wrap pt-10'>
              <div className='fs-6 fw-bold text-gray-700'>
                Showing {(leadStatus.currentPage - 1) * leadStatus.leadStatusesPerPage + 1} to{' '}
                {Math.min(
                  leadStatus.currentPage * leadStatus.leadStatusesPerPage,
                  leadStatus.totalLeadStatuses
                )}{' '}
                of {leadStatus.totalLeadStatuses} entries
              </div>

              <ul className='pagination'>
                {getPreviousPageButton()}
                {getPageList()}
                {getNextPageButton()}
              </ul>
            </div>
          )}
        </div>
        {/* begin::Body */}
      </div>

      {/* Add Lead Status Modal */}
      <Modal
        show={showModal}
        onHide={() => {
          setShowModal(false)
          formik.resetForm()
        }}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Add New Lead Status</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <form onSubmit={formik.handleSubmit} noValidate>
            <div className='fv-row mb-8'>
              <label className='form-label fw-bolder text-dark fs-6'>Status</label>
              <input
                placeholder='Hot'
                type='text'
                autoComplete='off'
                {...formik.getFieldProps('status')}
                className={`form-control bg-transparent ${
                  formik.touched.status && formik.errors.status ? 'is-invalid' : ''
                }`}
              />
              {formik.touched.status && formik.errors.status && (
                <div className='fv-plugins-message-container'>
                  <div className='fv-help-block'>
                    <span role='alert'>{formik.errors.status}</span>
                  </div>
                </div>
              )}
            </div>
            <div className='text-center'>
              <button
                type='button'
                onClick={() => {
                  setShowModal(false)
                  formik.resetForm()
                }}
                className='btn btn-light me-3'
              >
                Cancel
              </button>
              <button
                type='submit'
                className='btn btn-primary'
                disabled={formik.isSubmitting || !formik.isValid}
              >
                Add Status
              </button>
            </div>
          </form>
        </Modal.Body>
      </Modal>
    </>
  )
}

export default LeadStatusPage
